/**
 * Das Abzeichen eines Bots mit seinem ⓘ (`L3a`, **E104** Teilfrage 3, **E112**) — in der Lobby
 * und in der Spielerübersicht dieselbe Komponente. Was darin steht, sagt `botBadgeOf`.
 *
 * **ⓘ ist ein `<details>`**: aufklappbar mit Maus, Finger und Tastatur, ohne eigenen Zustand und
 * ohne `title` (den ein Finger nie zu sehen bekommt). Der Inhalt steht im DOM, auch zugeklappt.
 */

import { useTranslation } from 'react-i18next';

import type { LobbyBotDto } from '@flaghex/contracts';

import type { FallbackSummary } from '../game/history.js';
import { botBadgeOf } from './bot-badge.js';

export interface BotBadgeProps {
  /** Die Positivliste aus der Lobby-Ansicht — fehlt sie, steht „Bot" ohne ⓘ. */
  readonly bot: LobbyBotDto | undefined;
  /** Der ganze Satz für Vorlesewerkzeuge, wo das Etikett allein zu knapp ist. */
  readonly ariaLabel?: string | undefined;
  /** Die Rückfälle eines LLM-Bots (`L3b`) — nur im Spiel; die Lobby hat noch keine Züge. */
  readonly fallbacks?: FallbackSummary | undefined;
}

export function BotBadge({ bot, ariaLabel, fallbacks }: BotBadgeProps) {
  const { t } = useTranslation(['lobby']);
  const { label, info } = botBadgeOf(bot, t, fallbacks);

  return (
    <span className="bot-mark">
      <span className="badge" data-field="bot" aria-label={ariaLabel}>
        {label}
      </span>
      {info.length === 0 ? null : (
        <details className="bot-info">
          <summary aria-label={t('lobby:bot.badge.infoLabel')}>ⓘ</summary>
          <span className="bot-info-body" data-bot-info="">
            {info.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
        </details>
      )}
    </span>
  );
}
